import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useTenant } from '../../contexts/useTenant';
import { ConfirmModal } from '../../components/ConfirmModal';
import { TeamOnboardingChecklist } from '../../components/TeamOnboardingChecklist';
import { AuditLogTable } from '../../components/AuditLogTable';
import axios from 'axios';
import api from '../../services/authenticatedApi';
import { analytics } from '../../services/analytics/posthog';

import { AlertCircleIcon, ClockIcon, MailIcon, SettingsIcon, UserIcon } from './icons';
import { ConfirmModalState, Invitation, TabKey, TeamMember } from './types';
import { MembersTab } from './MembersTab';
import { InvitationsTab } from './InvitationsTab';
import { SettingsTab } from './SettingsTab';

const VALID_TABS: TabKey[] = ['members', 'invitations', 'settings', 'activity'];

export const TeamSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTenant, isMultitenancyEnabled, refreshTenants } = useTenant();

  const tabParam = searchParams.get('tab') as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'members',
  );

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteLoading, setInviteLoading] = useState(false);

  const [tenantName, setTenantName] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [resendingId, setResendingId] = useState<string | null>(null);

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(null);

  const currentUserRole = currentTenant?.role || 'member';
  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin' || isOwner;

  useEffect(() => {
    analytics.pageViewed('settings_team');
  }, []);

  useEffect(() => {
    if (!isMultitenancyEnabled) {
      navigate('/settings');
    }
  }, [isMultitenancyEnabled, navigate]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const t = searchParams.get('tab') as TabKey | null;
    if (t && VALID_TABS.includes(t)) {
      setActiveTab(t);
    }
  }, [searchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [membersRes, invitationsRes] = await Promise.all([
        api.get('/tenants/current/members'),
        api.get('/tenants/current/invitations'),
      ]);

      setMembers(membersRes.data.members || []);
      setInvitations(invitationsRes.data.invitations || []);
    } catch (err: unknown) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to load team data'
          : 'Failed to load team data',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentTenant) {
      loadData();
      setTenantName(currentTenant.name);
    }
  }, [currentTenant, loadData]);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post('/tenants/current/invitations', {
        email: inviteEmail,
        role: inviteRole,
      });

      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');

      const invitationsRes = await api.get('/tenants/current/invitations');
      setInvitations(invitationsRes.data.invitations || []);

      successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to send invitation'
          : 'Failed to send invitation',
      );
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await api.delete(`/tenants/current/invitations/${invitationId}`);
      setInvitations(invitations.filter((inv) => inv.id !== invitationId));
      setSuccess('Invitation cancelled');
      successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to cancel invitation'
          : 'Failed to cancel invitation',
      );
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    setResendingId(invitationId);
    setError(null);
    try {
      const response = await api.post(`/tenants/current/invitations/${invitationId}/resend`);
      const updatedInvitation = response.data.invitation;

      setInvitations(
        invitations.map((inv) =>
          inv.id === invitationId ? { ...inv, expiresAt: updatedInvitation.expiresAt } : inv,
        ),
      );

      setSuccess('Invitation resent successfully');
      successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message || err.response?.data?.error || 'Failed to resend invitation'
        : 'Failed to resend invitation';
      setError(message);
    } finally {
      setResendingId(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'member') => {
    try {
      await api.patch(`/tenants/current/members/${userId}`, { role: newRole });
      setMembers(members.map((m) => (m.userId === userId ? { ...m, role: newRole } : m)));
      setSuccess('Role updated');
      successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to update role'
          : 'Failed to update role',
      );
    }
  };

  const handleRemoveMember = (member: TeamMember) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${member.firstName || member.email} from the team? They will lose access to all team resources.`,
      confirmText: 'Remove',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/tenants/current/members/${member.userId}`);
          setMembers(members.filter((m) => m.userId !== member.userId));
          setSuccess('Member removed');
          successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
        } catch (err: unknown) {
          setError(
            axios.isAxiosError(err)
              ? err.response?.data?.error || 'Failed to remove member'
              : 'Failed to remove member',
          );
        }
      },
    });
  };

  const handleUpdateTenantName = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.patch('/tenants/current', { name: tenantName });
      await refreshTenants();
      setSuccess('Team name updated');
      successTimerRef.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to update team name'
          : 'Failed to update team name',
      );
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleRequestDeleteTeam = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Team',
      message: `Are you sure you want to delete "${currentTenant?.name}"? This will remove all team members and cannot be undone.`,
      confirmText: 'Delete Team',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await api.delete('/tenants/current');
          await refreshTenants();
          navigate('/dashboard');
        } catch (err: unknown) {
          setError(
            axios.isAxiosError(err)
              ? err.response?.data?.error || 'Failed to delete team'
              : 'Failed to delete team',
          );
        }
      },
    });
  };

  if (!isMultitenancyEnabled) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading team settings...</p>
        </div>
      </div>
    );
  }

  const pendingCount = invitations.filter((i) => i.status === 'pending').length;

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle="Team Settings" showDivider={true} />

      <main className="flex-1 max-w-7xl mx-auto py-8 pt-24 w-full">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Team Settings</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your team members, invitations, and settings for{' '}
            <span className="font-medium text-teal-600 dark:text-teal-400">
              {currentTenant?.name}
            </span>
          </p>
        </div>

        <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => handleTabChange('members')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'members'
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <UserIcon className="w-4 h-4" />
              Members
            </button>
            <button
              onClick={() => handleTabChange('invitations')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'invitations'
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <MailIcon className="w-4 h-4" />
              Invitations
              {pendingCount > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
            {isOwner && (
              <button
                onClick={() => handleTabChange('settings')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'settings'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <SettingsIcon className="w-4 h-4" />
                Settings
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => handleTabChange('activity')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'activity'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <ClockIcon className="w-4 h-4" />
                Activity
              </button>
            )}
          </nav>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div className="flex">
              <AlertCircleIcon className="w-4 h-4 text-red-400 shrink-0" />
              <div className="ml-2 text-sm text-red-700 dark:text-red-300">{error}</div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
            <div className="text-sm text-green-700 dark:text-green-300">{success}</div>
          </div>
        )}

        {currentTenant && isOwner && (
          <TeamOnboardingChecklist
            tenant={currentTenant}
            memberCount={members.length}
            hasConnectedRepo={false}
            hasSetupBilling={false}
          />
        )}

        {activeTab === 'members' && (
          <MembersTab
            members={members}
            isOwner={isOwner}
            onRoleChange={handleRoleChange}
            onRemoveMember={handleRemoveMember}
          />
        )}

        {activeTab === 'invitations' && (
          <InvitationsTab
            invitations={invitations}
            isAdmin={isAdmin}
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            inviteRole={inviteRole}
            setInviteRole={setInviteRole}
            inviteLoading={inviteLoading}
            resendingId={resendingId}
            onInvite={handleInvite}
            onResend={handleResendInvitation}
            onCancel={handleCancelInvitation}
          />
        )}

        {activeTab === 'settings' && isOwner && (
          <SettingsTab
            tenantName={tenantName}
            setTenantName={setTenantName}
            currentTenantName={currentTenant?.name}
            settingsLoading={settingsLoading}
            onUpdateTenantName={handleUpdateTenantName}
            onRequestDeleteTeam={handleRequestDeleteTeam}
          />
        )}

        {activeTab === 'activity' && isAdmin && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <AuditLogTable tenantId={currentTenant?.id} />
          </div>
        )}
      </main>

      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={async () => {
            await confirmModal.onConfirm();
            setConfirmModal(null);
          }}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText="Cancel"
          variant={confirmModal.variant}
        />
      )}

      <Footer />
    </div>
  );
};

export default TeamSettingsPage;
